// client/src/components/CroquettesSettings.js
import React, { useEffect, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";

// -----------------------------
// Constantes + helpers (autonome)
// -----------------------------
const LIFE_STAGES = [
  { value: "puppy", label: "Chiot / Junior" },
  { value: "adult", label: "Adulte" },
  { value: "senior", label: "Senior" },
  { value: "all", label: "Tous âges" },
];

const SIZE_TARGETS = [
  { value: "xs", label: "Très petit (< 5 kg)" },
  { value: "s", label: "Petit (< 10 kg)" },
  { value: "m", label: "Moyen (10–25 kg)" },
  { value: "l", label: "Grand (25–45 kg)" },
  { value: "xl", label: "Très grand (> 45 kg)" },
  { value: "all", label: "Toutes tailles" },
];

const GRAIN_LEVELS = [
  { value: "grain", label: "Avec céréales" },
  { value: "low_grain", label: "Low grain (faible céréales)" },
  { value: "grain_free", label: "Sans céréales (grain free)" },
];

const PROTEIN_SOURCES = ["poulet", "poisson", "agneau", "boeuf", "dinde", "canard", "insectes", "porc", "oeuf"];

const CARB_SOURCES = [
  "riz",
  "riz_complet",
  "pommes_de_terre",
  "patate_douce",
  "sarrasin",
  "avoine",
  "mais",
  "ble",
  "orge",
  "pois",
  "lentilles",
  "citrouille",
];

const FUNCTIONAL_BENEFITS = [
  { key: "digestion", label: "Digestion / transit" },
  { key: "probiotics", label: "Probiotiques / prébiotiques" },
  { key: "skin_coat", label: "Peau & pelage (oméga 3/6)" },
  { key: "joints", label: "Articulations (glucosamine/chondroïtine)" },
  { key: "immunity", label: "Immunité" },
  { key: "weight", label: "Gestion du poids" },
  { key: "stress_relax", label: "Relax / anti-stress" },
  { key: "dental", label: "Dentaire" },
];

const EXCLUSIONS = ["gluten", "ble", "mais", "soja", "colorants_artificiels", "conservateurs_artificiels"];

function uniq(arr) {
  return Array.from(new Set((arr || []).filter(Boolean)));
}

function buildProductTags(p) {
  const tags = [];
  (p.lifeStages || []).forEach((x) => tags.push(`life:${x}`));
  (p.sizeTargets || []).forEach((x) => tags.push(`size:${x}`));

  if (p.primaryProtein) tags.push(`protein:${p.primaryProtein}`);
  (p.proteinSources || []).forEach((x) => tags.push(`protein:${x}`));
  (p.carbSources || []).forEach((x) => tags.push(`carb:${x}`));

  if (p.grainLevel) tags.push(`grain:${p.grainLevel}`);
  (p.benefits || []).forEach((b) => tags.push(`benefit:${b}`));
  (p.exclusions || []).forEach((e) => tags.push(`exclude:${e}`));

  if (p.hypoallergenic) tags.push("flag:hypoallergenic");
  if (p.sterilisedSuitable) tags.push("flag:sterilised");
  if (p.weightControl) tags.push("flag:weight_control");

  return uniq(tags);
}


export default function CroquettesSettings() {
  // produits
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "Croquettes"), orderBy("updatedAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingProducts(false);
      },
      () => setLoadingProducts(false)
    );
    return () => unsub();
  }, []);

  // form
  const [form, setForm] = useState({
    brand: "",
    name: "",
    sku: "",
    barcode: "",
    bagWeightKg: 12,
    price: "",
    currency: "EUR",

    lifeStages: ["adult"],
    sizeTargets: ["all"],

    primaryProtein: "poulet",
    proteinSources: ["poulet"],
    carbSources: ["riz"],
    grainLevel: "low_grain",

    hypoallergenic: false,
    sterilisedSuitable: false,
    weightControl: false,

    benefits: ["digestion"],
    exclusions: ["colorants_artificiels", "conservateurs_artificiels"],

    annotation: "",
  });

  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  function setField(key, value) {
    setForm((p) => ({ ...p, [key]: value }));
  }
  function toggleArrayValue(key, value) {
    setForm((p) => {
      const arr = new Set(p[key] || []);
      if (arr.has(value)) arr.delete(value);
      else arr.add(value);
      return { ...p, [key]: Array.from(arr) };
    });
  }

  function normalizeFormForSave(raw) {
    const p = { ...raw };

    p.brand = (p.brand || "").trim();
    p.name = (p.name || "").trim();
    p.sku = (p.sku || "").trim();
    p.barcode = (p.barcode || "").trim();
    p.currency = (p.currency || "EUR").trim().toUpperCase();

    p.bagWeightKg = Number(p.bagWeightKg || 0);
    p.price = p.price === "" ? null : Number(p.price);

    p.lifeStages = uniq(p.lifeStages);
    p.sizeTargets = uniq(p.sizeTargets);

    p.primaryProtein = (p.primaryProtein || "").trim().toLowerCase();
    p.proteinSources = uniq((p.proteinSources || []).map((x) => String(x).toLowerCase()));
    p.carbSources = uniq((p.carbSources || []).map((x) => String(x).toLowerCase()));

    if (p.primaryProtein && !p.proteinSources.includes(p.primaryProtein)) {
      p.proteinSources = uniq([...p.proteinSources, p.primaryProtein]);
    }

    p.grainLevel = p.grainLevel || "low_grain";
    p.benefits = uniq(p.benefits);
    p.exclusions = uniq(p.exclusions);
    p.annotation = (p.annotation || "").trim();

    p.tags = buildProductTags(p);
    return p;
  }

  async function handleCreateProduct(e) {
    e.preventDefault();
    setSaveMsg("");

    const p = normalizeFormForSave(form);

    if (!p.brand || !p.name) {
      setSaveMsg("❌ Marque et nom sont obligatoires.");
      return;
    }
    if (!p.bagWeightKg || p.bagWeightKg <= 0) {
      setSaveMsg("❌ Poids du sac invalide.");
      return;
    }

    setSaving(true);
    try {
      const now = serverTimestamp();

      await addDoc(collection(db, "Croquettes"), {
        brand: p.brand,
        name: p.name,
        sku: p.sku || null,
        barcode: p.barcode || null,
        bagWeightKg: p.bagWeightKg,
        price: p.price,
        currency: p.currency,

        primaryProtein: p.primaryProtein || null,
        proteinSources: p.proteinSources,
        carbSources: p.carbSources,
        grainLevel: p.grainLevel,

        lifeStages: p.lifeStages,
        sizeTargets: p.sizeTargets,

        hypoallergenic: !!p.hypoallergenic,
        sterilisedSuitable: !!p.sterilisedSuitable,
        weightControl: !!p.weightControl,

        benefits: p.benefits,
        exclusions: p.exclusions,

        annotation: p.annotation || null,

        tags: p.tags,

        createdAt: now,
        updatedAt: now,
      });

      setSaveMsg("✅ Produit créé !");
      setForm((prev) => ({
        ...prev,
        name: "",
        sku: "",
        barcode: "",
        price: "",
        annotation: "",
      }));
    } catch (err) {
      console.error(err);
      setSaveMsg("❌ Erreur lors de la création du produit.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteProduct(productId) {
    if (!window.confirm("Supprimer ce produit ?")) return;
    try {
      await deleteDoc(doc(db, "Croquettes", productId));
    } catch (e) {
      console.error(e);
      alert("Erreur suppression.");
    }
  }

  const css = `
.s-wrap{
  --bg:var(--eb-bg);
  --card:var(--eb-surface);
  --card2:var(--eb-surface);
  --stroke:var(--eb-border);
  --text:var(--eb-text);
  --muted:var(--eb-muted);
  --shadow:var(--eb-shadow-sm);
  --r:var(--eb-radius-lg);
  --r2:var(--eb-radius);
  --pad:20px;
}

.s-wrap{
  min-height:100vh;
  color:var(--text);
  background:
    radial-gradient(1200px 600px at 50% -10%, rgba(39, 76, 116, 0.08), transparent 60%),
    var(--bg);
  padding: 26px 16px;
}

.s-container{ max-width: var(--eb-content-max, 1240px); margin: 0 auto; }

.s-topbar{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.s-leftTop{
  display:flex;
  flex-direction:column;
  gap:4px;
}

.s-leftTop h1{
  margin:0;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.s-sub{
  color:var(--muted);
  font-size: 13px;
}

.s-back{
  border:1px solid var(--stroke);
  background:var(--eb-surface);
  color: var(--text);
  border-radius: var(--eb-radius);
  padding: 10px 12px;
  cursor:pointer;
  display:flex;
  align-items:center;
  gap:8px;
  box-shadow: var(--shadow);
  transition: transform .12s ease, box-shadow .12s ease;
  white-space: nowrap;
}

.s-back:hover{
  transform: translateY(-1px);
  box-shadow: var(--eb-shadow-md);
}

.s-grid{
  display:grid;
  grid-template-columns: minmax(0,1fr) minmax(0,1fr);
  gap:20px;
  align-items: start;
}
@media (max-width: 980px){
  .s-grid{ grid-template-columns:1fr; }
}

.s-card{
  border:1px solid var(--stroke);
  background: var(--card);
  border-radius: var(--r);
  box-shadow: var(--shadow);
  padding: var(--pad);
}

.s-card h2{
  margin:0 0 12px 0;
  font-size:16px;
}

.s-row4{
  display:grid;
  grid-template-columns: repeat(4, minmax(0,1fr));
  gap:10px;
}
.s-row3{
  display:grid;
  grid-template-columns: repeat(3, minmax(0,1fr));
  gap:10px;
}
.s-row2{
  display:grid;
  grid-template-columns: repeat(2, minmax(0,1fr));
  gap:10px;
}

@media (max-width: 760px){
  .s-row4,
  .s-row3,
  .s-row2{ grid-template-columns:1fr; }
  .s-wrap{ padding: 20px 14px 96px; }
  .s-card{ --pad:16px; }
}

.s-field label{
  display:block;
  font-size:13px;
  color: var(--muted);
  margin-bottom:6px;
}

.s-field input,
.s-field select,
.s-field textarea{
  width:100%;
  border:1px solid var(--stroke);
  background:var(--eb-surface);
  color: var(--text);
  padding: 10px 12px;
  border-radius: 12px;
  outline:none;
  resize: vertical;
}

.s-pillbox{
  display:flex;
  flex-wrap:wrap;
  gap:8px;
}

.s-pill{
  border:1px solid var(--stroke);
  background:var(--eb-surface-2);
  color: var(--text);
  padding: 8px 10px;
  border-radius: 999px;
  font-size: 13px;
  cursor:pointer;
  user-select:none;
}

.s-pill.on{
  background:var(--eb-brand-100);
  border-color:var(--eb-brand-300);
  color:var(--eb-brand);
}

.s-check{
  display:flex;
  align-items:center;
  gap:10px;
  border:1px solid var(--stroke);
  background:var(--eb-surface-2);
  padding: 10px 12px;
  border-radius: var(--eb-radius);
}

.s-btn{
  border:1px solid var(--stroke);
  background:var(--eb-surface);
  color: var(--text);
  border-radius: var(--eb-radius);
  padding: 10px 12px;
  cursor:pointer;
  font-weight: 800;
  box-shadow: var(--shadow);
}

.s-btn:hover{
  box-shadow: var(--eb-shadow-md);
}

.s-danger{
  background:var(--eb-danger-bg);
  border-color:#fbd0cb;
  color:var(--eb-danger);
}

.s-muted{
  color: var(--muted);
  font-size: 13px;
}

.s-prod{
  border:1px solid var(--stroke);
  background:var(--eb-surface);
  border-radius: var(--eb-radius);
  padding: 12px;
  margin-top: 10px;
}

.s-prodTop{
  display:flex;
  justify-content:space-between;
  gap:10px;
  flex-wrap:wrap;
}

.s-prodName{
  font-weight: 700;
}

.s-badges{
  color: var(--muted);
  font-size: 13px;
  margin-top: 6px;
}

.s-anno{
  margin-top: 10px;
  border:1px solid var(--stroke);
  border-radius: var(--eb-radius);
  padding: 10px 12px;
  background:var(--eb-surface-2);
  white-space: pre-wrap;
  font-size: 13px;
  color: var(--eb-text-2);
}

.s-wrap input:focus, .s-wrap select:focus, .s-wrap textarea:focus{
  border-color: var(--eb-brand-500);
  box-shadow: var(--eb-ring);
}
`;


  return (
    <div className="s-wrap">
      <style>{css}</style>

      <div className="s-container">
        <div className="s-topbar">
          <div className="s-leftTop">
            <h1>Paramètres croquettes</h1>
            <div className="s-sub">Créer / gérer les produits (collection Firestore: Croquettes)</div>
          </div>


        <button className="s-back" onClick={() => (window.location.href = "/Croquettes")} aria-label="Retour">
            ← Retour
        </button>
        </div>

        <div className="s-grid">
          {/* CREATION */}
          <section className="s-card">
            <h2>Créer un produit</h2>

            <form onSubmit={handleCreateProduct} style={{ display: "grid", gap: 12 }}>
              <div className="s-row4">
                <div className="s-field">
                  <label>Marque *</label>
                  <input value={form.brand} onChange={(e) => setField("brand", e.target.value)} placeholder="Ultra Premium Direct" />
                </div>
                <div className="s-field">
                  <label>Nom produit *</label>
                  <input value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Adulte Poulet Low Grain" />
                </div>
                <div className="s-field">
                  <label>SKU (optionnel)</label>
                  <input value={form.sku} onChange={(e) => setField("sku", e.target.value)} placeholder="UPD-ADULT-12KG" />
                </div>
                <div className="s-field">
                  <label>Code-barres (optionnel)</label>
                  <input value={form.barcode} onChange={(e) => setField("barcode", e.target.value)} placeholder="3760..." />
                </div>
              </div>

              <div className="s-row3">
                <div className="s-field">
                  <label>Poids du sac (kg) *</label>
                  <input
                    type="number"
                    min={0.1}
                    step="0.1"
                    value={form.bagWeightKg}
                    onChange={(e) => setField("bagWeightKg", e.target.value)}
                  />
                </div>
                <div className="s-field">
                  <label>Prix (optionnel)</label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.price}
                    onChange={(e) => setField("price", e.target.value)}
                    placeholder="Ex: 59.90"
                  />
                </div>
                <div className="s-field">
                  <label>Devise</label>
                  <select value={form.currency} onChange={(e) => setField("currency", e.target.value)}>
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </div>

              <div className="s-card" style={{ boxShadow: "none", padding: 14, background: "var(--eb-surface-2)" }}>
                <h2 style={{ marginBottom: 10 }}>Ciblage</h2>

                <div className="s-row2">
                  <div>
                    <div className="s-muted" style={{ marginBottom: 8 }}>Âges ciblés</div>
                    <div className="s-pillbox">
                      {LIFE_STAGES.map((x) => (
                        <div
                          key={x.value}
                          className={`s-pill ${(form.lifeStages || []).includes(x.value) ? "on" : ""}`}
                          onClick={() => toggleArrayValue("lifeStages", x.value)}
                          role="button"
                          tabIndex={0}
                        >
                          {x.label}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="s-muted" style={{ marginBottom: 8 }}>Tailles ciblées</div>
                    <div className="s-pillbox">
                      {SIZE_TARGETS.map((x) => (
                        <div
                          key={x.value}
                          className={`s-pill ${(form.sizeTargets || []).includes(x.value) ? "on" : ""}`}
                          onClick={() => toggleArrayValue("sizeTargets", x.value)}
                          role="button"
                          tabIndex={0}
                        >
                          {x.label}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="s-card" style={{ boxShadow: "none", padding: 14, background: "var(--eb-surface-2)" }}>
                <h2 style={{ marginBottom: 10 }}>Recette</h2>

                <div className="s-row3">
                  <div className="s-field">
                    <label>Protéine principale</label>
                    <select value={form.primaryProtein} onChange={(e) => setField("primaryProtein", e.target.value)}>
                      {PROTEIN_SOURCES.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>

                  <div className="s-field">
                    <label>Niveau de céréales</label>
                    <select value={form.grainLevel} onChange={(e) => setField("grainLevel", e.target.value)}>
                      {GRAIN_LEVELS.map((g) => (
                        <option key={g.value} value={g.value}>{g.label}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{ display: "grid", gap: 10 }}>
                    <div className="s-check">
                      <input
                        type="checkbox"
                        checked={form.hypoallergenic}
                        onChange={(e) => setField("hypoallergenic", e.target.checked)}
                      />
                      <b>Hypoallergénique</b>
                    </div>
                    <div className="s-check">
                      <input
                        type="checkbox"
                        checked={form.sterilisedSuitable}
                        onChange={(e) => setField("sterilisedSuitable", e.target.checked)}
                      />
                      <b>Convient stérilisé</b>
                    </div>
                    <div className="s-check">
                      <input
                        type="checkbox"
                        checked={form.weightControl}
                        onChange={(e) => setField("weightControl", e.target.checked)}
                      />
                      <b>Weight control</b>
                    </div>
                  </div>
                </div>

                <div style={{ height: 12 }} />

                <div className="s-row2">
                  <div>
                    <div className="s-muted" style={{ marginBottom: 8 }}>Protéines présentes</div>
                    <div className="s-pillbox">
                      {PROTEIN_SOURCES.map((p) => (
                        <div
                          key={p}
                          className={`s-pill ${(form.proteinSources || []).includes(p) ? "on" : ""}`}
                          onClick={() => toggleArrayValue("proteinSources", p)}
                          role="button"
                          tabIndex={0}
                        >
                          {p}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="s-muted" style={{ marginBottom: 8 }}>Sources de glucides</div>
                    <div className="s-pillbox">
                      {CARB_SOURCES.map((c) => (
                        <div
                          key={c}
                          className={`s-pill ${(form.carbSources || []).includes(c) ? "on" : ""}`}
                          onClick={() => toggleArrayValue("carbSources", c)}
                          role="button"
                          tabIndex={0}
                        >
                          {c}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="s-row2">
                <div className="s-card" style={{ boxShadow: "none", padding: 14, background: "var(--eb-surface-2)" }}>
                  <h2 style={{ marginBottom: 10 }}>Bénéfices</h2>
                  <div className="s-pillbox">
                    {FUNCTIONAL_BENEFITS.map((b) => (
                      <div
                        key={b.key}
                        className={`s-pill ${(form.benefits || []).includes(b.key) ? "on" : ""}`}
                        onClick={() => toggleArrayValue("benefits", b.key)}
                        role="button"
                        tabIndex={0}
                      >
                        {b.label}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="s-card" style={{ boxShadow: "none", padding: 14, background: "var(--eb-surface-2)" }}>
                  <h2 style={{ marginBottom: 10 }}>Exclusions garanties</h2>
                  <div className="s-pillbox">
                    {EXCLUSIONS.map((e) => (
                      <div
                        key={e}
                        className={`s-pill ${(form.exclusions || []).includes(e) ? "on" : ""}`}
                        onClick={() => toggleArrayValue("exclusions", e)}
                        role="button"
                        tabIndex={0}
                      >
                        {e}
                      </div>
                    ))}
                  </div>
                  <div className="s-muted" style={{ marginTop: 8 }}>
                    Ex: cocher “gluten” = “sans gluten”
                  </div>
                </div>
              </div>

              <div className="s-field">
                <label>Annotation générale (optionnel)</label>
                <textarea
                  rows={4}
                  value={form.annotation}
                  onChange={(e) => setField("annotation", e.target.value)}
                  placeholder="Ex: Très appétent. Top chiens difficiles. Attention stock."
                />
              </div>

              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <button className="s-btn" type="submit" disabled={saving}>
                  {saving ? "Enregistrement..." : "Créer le produit"}
                </button>
                {saveMsg && <div className="s-muted">{saveMsg}</div>}
              </div>
            </form>
          </section>

          {/* LISTING */}
          <section className="s-card">
            <h2>Produits existants</h2>

            {loadingProducts ? (
              <div className="s-muted">Chargement…</div>
            ) : products.length === 0 ? (
              <div className="s-muted">Aucun produit enregistré.</div>
            ) : (
              <div>
                <div className="s-muted">{products.length} produit(s)</div>

                {products.map((p) => (
                  <div key={p.id} className="s-prod">
                    <div className="s-prodTop">
                      <div>
                        <div className="s-prodName">{p.brand} — {p.name}</div>
                        <div className="s-badges">
                          {p.bagWeightKg} kg • {p.price != null ? `${p.price} ${p.currency || ""}` : "prix —"} •{" "}
                          {p.primaryProtein ? `protéine: ${p.primaryProtein}` : "protéine —"} •{" "}
                          {p.grainLevel ? `grain: ${p.grainLevel}` : "grain —"}
                        </div>
                        <div className="s-badges" style={{ marginTop: 6 }}>
                          Âges: {(p.lifeStages || []).join(", ") || "—"} • Tailles: {(p.sizeTargets || []).join(", ") || "—"}
                        </div>
                        <div className="s-badges" style={{ marginTop: 6 }}>
                          Bénéfices: {(p.benefits || []).join(", ") || "—"}
                        </div>
                      </div>

                      <button className="s-btn s-danger" onClick={() => handleDeleteProduct(p.id)}>
                        Supprimer
                      </button>
                    </div>

                    {p.annotation && <div className="s-anno"><b>Annotation :</b> {p.annotation}</div>}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
